import * as React from 'react'
import {MERCHANT, SKUS, COMBOS, PACKS, SUBCATS, DWS_RECORDS, REQUESTS, MERGE_CANDIDATES} from '../data/noberoFreeze'
import {NOW, EVENT_KIND, APPROVAL_NOTICE} from '../data/reference'
import {volumetricKg, chargeableKg} from '../weightMath'
import {packById} from '../packing/state/packingModel'
import {
  applyMerge, applyUndoMerge, applyImport, applyOmsPull,
  applyReview, applyMarkReviewed, applyFixCode,
} from '../catalogue/catalogueState'
import {applySubmission, todayISO} from '../requests/logic'

/**
 * Weight Freeze (v2) — the ONE shared store for the whole module.
 *
 * Why this exists: `index.js` renders exactly one section at a time and
 * unmounts whichever one isn't active. Six section plans (Catalogue,
 * Packing, DWS, Initiate, Requests, Analytics) were each written against
 * their OWN section-local `useState` layered on top of the frozen dataset
 * import. That means every one of those mutations — a Catalogue merge, a
 * Packing material's dimension edit, a DWS upload, a raised freeze request —
 * would vanish the instant the merchant clicked to another rail item and
 * back, because the component holding that `useState` had just been
 * unmounted. Worse, Packing's cascade rewrites `chargeKg` on rows that
 * Catalogue and Analytics also render, so two sections could show two
 * different weights for the same SKU if each kept its own copy. And
 * Initiate raising a request into its own state while Requests read a
 * competing `useState(REQUESTS)` meant a raised request would simply never
 * appear on the Requests page.
 *
 * The fix: hold ALL of the module's mutable state in one hook, instantiated
 * once in `index.js` ABOVE the section switch (see freeze-v2/index.js), and
 * thread it down to every section. Every section reads and writes the same
 * `skus`/`combos`/`packs`/etc. arrays, so navigating the rail never loses
 * anything and no two sections can ever disagree about the same row.
 *
 * NOTHING PERSISTS ACROSS A PAGE RELOAD. This is a dummy-data module with no
 * backend: `useFreezeStore` seeds its state by deep-cloning the frozen
 * `noberoFreeze.js` exports once, on mount, and every action below only
 * ever produces new, local, in-memory state on top of that clone. A reload
 * re-imports the frozen module and starts over from the original seed. That
 * is expected behaviour, exactly like `disputes-v2/actions.js`'s own
 * `applyTransition` — do not mistake it for a bug or wire up any kind of
 * storage for it.
 *
 * THE FROZEN IMPORTS ARE NEVER MUTATED. `SKUS`, `COMBOS`, `PACKS`, `SUBCATS`,
 * `DWS_RECORDS`, `REQUESTS` and `MERGE_CANDIDATES` are module-level constants
 * shared by reference with every other file that imports `noberoFreeze.js`
 * (including its own test suite and, transitively, `SopPage.js`). Mutating
 * them in place would leak across every consumer and across test files
 * sharing the same Jest module cache. `initialState()` below deep-clones
 * each export via JSON round-trip (safe: the dataset is plain, JSON-safe
 * data with no functions, Dates or Sets in it) before it ever enters React
 * state, and every reducer case below builds brand-new objects/arrays with
 * spreads or `.map`/`.filter` rather than mutating in place — so nothing
 * downstream of that initial clone can ever reach back into the import.
 *
 * CATALOGUE BRIDGE (Task 4). Every catalogue transition — merge, undo-merge,
 * import, an OMS/WMS pull, a review save, mark-reviewed, a code fix — is a
 * pure function in `../catalogue/catalogueState.js`, built and tested against
 * `../catalogue/catalogueMath.js`'s own `rollUp`/`combine`. This reducer does
 * not re-implement any of that: each catalogue case below just hands the
 * store's current state to the matching `applyX` function and writes back
 * whatever it returns. Two consequences worth stating explicitly:
 *   1. A merge here no longer rewrites the mother row's own `hist`/`ships`/
 *      `mpw`/`spread` in place (the pre-bridge `MERGE_SKUS` used to, via the
 *      now-removed `computeMpw`/`computeSpread`/`mergeHist` helpers below).
 *      The combined view is always DERIVED on read via `rollUp(state.skus)`
 *      — see `CataloguePage`'s own `roll` — exactly like Catalogue's table.
 *      Keeping the old denormalised copy here would have been a second,
 *      divergent merge implementation, which is precisely what Section 0
 *      exists to prevent.
 *   2. `mergeSkus`'s signature changed from `(motherSku, childSku)` to
 *      `(mother, codes)` — a mother plus the merchant's FULL ticked
 *      selection — to match `catalogueState.applyMerge`, which expands any
 *      code that is already a mother so a merge always ends in one flat
 *      parent. The old 2-arg shape had exactly one caller: this file's own
 *      test, which is updated alongside it.
 * `mapLog` (the in-session merge/undo log) and `catalogueSyncing` (whether an
 * OMS/WMS pull is in flight) are new state fields for the same reason as
 * everything else here: Catalogue mounts and unmounts with the rail, so both
 * have to live above the section switch to survive a rail click — including
 * a pull that is still running when the merchant navigates away (see
 * `pullFromOms` below, which schedules its own completion on a ref owned by
 * this hook, not by whichever component happened to call it).
 */

function deepClone(value) {
  return JSON.parse(JSON.stringify(value))
}

/** 'Prozo Ops' when Prozo initiates this merchant's freezes, else the merchant's own name — the same derivation SopPage uses for its own `raiser`. Single-sourced here so a mapLog entry's `by` and the store's own exposed `actor` can never disagree. */
function catalogueActor() {
  return (MERCHANT.type && MERCHANT.type.freeze === 'prozo') ? 'Prozo Ops' : MERCHANT.name
}

// Matches the brief's own simulated OMS/WMS connector latency.
const OMS_LATENCY_MS = 1200

/**
 * `live` (index.js, the real route): the REQUESTS list starts EMPTY and is filled from the backend
 * by `setRequests` — the two live sections (Requests & statuses, Raise a request) must never show
 * the Nobero fixtures beside real requests. Every other slice stays seeded until its section goes
 * live, and index.js says so on screen.
 */
function initialState(live) {
  return {
    live: !!live,
    combos: deepClone(COMBOS),
    dwsRecords: deepClone(DWS_RECORDS),
    requests: live ? [] : deepClone(REQUESTS),
    // Live mounts start EMPTY for every slice the server now owns. Seeding them from the frozen
    // dataset and replacing it a moment later would flash another merchant's catalogue on screen —
    // Nobero's SKUs, in someone else's panel. Empty until the fetch lands is the honest state.
    skus: live ? [] : deepClone(SKUS),
    packs: live ? [] : deepClone(PACKS),
    subcats: live ? {} : deepClone(SUBCATS),
    // Session-only: records added via `addUpload` land here, never in
    // `dwsRecords`. `dwsRecords` is the seeded historical evidence locker;
    // `uploads` is the (initially empty) audit trail of what THIS session
    // added on top of it. A section rendering "all evidence for this row"
    // reads both — `[...dwsRecords, ...uploads].filter(...)` — so it can
    // still tell seeded history apart from what was just uploaded.
    uploads: [],
    mergeCandidates: deepClone(MERGE_CANDIDATES),
    // Catalogue-only, session-local, never seeded from noberoFreeze.js —
    // see catalogueState.js's own `initialState`, which this bridges to.
    mapLog: [],
    catalogueSyncing: false,
  }
}

function reducer(state, action) {
  switch (action.type) {
    // Merge/undo-merge/import/review/mark-reviewed/fix-code all delegate to
    // catalogueState.js's own pure functions — see this file's header for
    // why. `at`/`by` are sourced from the pinned `NOW` and the computed
    // `catalogueActor()` here, never from the caller, so every mapLog entry
    // is stamped consistently regardless of which UI triggered it.
    case 'MERGE_SKUS': {
      const {mother, codes} = action
      const result = applyMerge(state, {mother, codes, at: NOW.toISOString(), by: catalogueActor()})
      return {...state, skus: result.skus, mapLog: result.mapLog}
    }

    case 'UNDO_MERGE_SKU': {
      const result = applyUndoMerge(state, {entryId: action.entryId, at: NOW.toISOString(), by: catalogueActor()})
      return {...state, skus: result.skus, mapLog: result.mapLog}
    }

    case 'IMPORT_CATALOGUE': {
      return applyImport(state, {rows: action.rows, packs: state.packs})
    }

    // Two-step so a pull survives a rail click: START flips the flag (and is
    // a no-op if one is already running), and the actual pull only lands
    // when APPLY fires — scheduled by `pullFromOms` below on a timer owned
    // by this hook, not by Catalogue's own component tree.
    case 'START_OMS_PULL': {
      if (state.catalogueSyncing) return state
      return {...state, catalogueSyncing: true}
    }

    case 'APPLY_OMS_PULL': {
      if (!state.catalogueSyncing) return state
      const next = applyOmsPull(state, {merchantName: MERCHANT.name, packs: state.packs})
      return {...next, catalogueSyncing: false}
    }

    case 'REVIEW_SKU': {
      return applyReview(state, {code: action.code, patch: action.patch, packs: state.packs})
    }

    case 'MARK_SKUS_REVIEWED': {
      return applyMarkReviewed(state, {codes: action.codes})
    }

    case 'FIX_SKU_CODE': {
      return applyFixCode(state, {code: action.code, nextCode: action.nextCode})
    }

    // Generic field patch on one SKU row — Catalogue's edit surface (dq
    // flag, cat/sub reassignment, review confirmation, etc.).
    case 'UPDATE_SKU': {
      const {sku, patch} = action
      const skus = state.skus.map((s) => (s.sku === sku ? {...s, ...patch} : s))
      return {...state, skus}
    }

    // Adds a new packing material (when no pack with this `id` exists yet)
    // or patches an existing one. Whenever the resulting pack carries
    // `dims`, `volKg` is recomputed from those dims via weightMath's own
    // `volumetricKg` — never inline arithmetic — and that becomes the new
    // volumetric weight for EVERY sku/combo currently assigned this
    // `packId`, cascading into `chargeKg = max(deadKg, volKg)` via
    // weightMath's `chargeableKg` on each of them. This is the cascade the
    // whole module depends on: skip either the SKUS or the COMBOS pass and
    // the two would silently disagree on the same packing material's true
    // volumetric weight.
    case 'UPSERT_PACK': {
      const {patch} = action
      const idx = state.packs.findIndex((p) => p.id === patch.id)
      const base = idx === -1 ? {} : state.packs[idx]
      const merged = {...base, ...patch}
      merged.volKg = merged.dims ? volumetricKg(merged.dims) : null

      const packs = idx === -1
        ? [...state.packs, merged]
        : state.packs.map((p, i) => (i === idx ? merged : p))

      const newVolKg = merged.volKg || 0
      const skus = state.skus.map((s) => (
        s.packId === merged.id ? {...s, volKg: newVolKg, chargeKg: chargeableKg(s.deadKg, newVolKg)} : s
      ))
      const combos = state.combos.map((c) => (
        c.packId === merged.id ? {...c, volKg: newVolKg, chargeKg: chargeableKg(c.deadKg, newVolKg)} : c
      ))

      return {...state, packs, skus, combos}
    }

    // Removes a packing material outright, reassigning every sku/combo that
    // carried it to `destId` (via packingModel's own `packById` lookup —
    // never a second `.find` written out here). No destination means no
    // material at all: `packId: null`, which is a legitimate "Not set"
    // state per packingModel's `packLabel`, not a dangling reference to an
    // id that no longer resolves. When the destination itself carries
    // dimensions its `volKg` takes over; otherwise the row's existing
    // `volKg` is left alone (a flyer implies no volumetric figure, not a
    // zero one) and only `chargeKg` is re-derived via weightMath's
    // `chargeableKg` — the same cascade shape as `UPSERT_PACK` above, so
    // the two can never disagree about what "this material's weight" means.
    case 'REMOVE_PACK': {
      const {id, destId} = action
      const dest = destId ? packById(state.packs, destId) : null
      const useDestVol = !!dest && dest.volKg != null

      const packs = state.packs.filter((p) => p.id !== id)
      const skus = state.skus.map((s) => {
        if (s.packId !== id) return s
        const volKg = useDestVol ? dest.volKg : s.volKg
        return {...s, packId: dest ? dest.id : null, volKg, chargeKg: chargeableKg(s.deadKg, volKg)}
      })
      const combos = state.combos.map((c) => {
        if (c.packId !== id) return c
        const volKg = useDestVol ? dest.volKg : c.volKg
        return {...c, packId: dest ? dest.id : null, volKg, chargeKg: chargeableKg(c.deadKg, volKg)}
      })

      return {...state, packs, skus, combos}
    }

    // Adds `sub` under `cat` in the sub-categories map, creating `cat` if
    // needed. Kept sorted, matching gen-nobero-freeze.js's own convention
    // for this same field.
    case 'UPSERT_SUBCAT': {
      const {cat, sub} = action
      const existing = state.subcats[cat] || []
      if (existing.includes(sub)) return state
      const subcats = {...state.subcats, [cat]: [...existing, sub].sort()}
      return {...state, subcats}
    }

    // Removes `sub` from `cat`, reassigning every SKU that carried it to
    // `dest` (`{cat, sub}`). SKUs are never deleted here — they keep
    // shipping — so a populated sub-category MUST name somewhere for them
    // to go: this throws rather than silently orphaning them when the
    // sub-category has members and no `dest` was given (the mockup's own
    // bug — it dropped them on the floor). Drops `cat` entirely once its
    // last sub-category is gone, same invariant as `UPSERT_SUBCAT`, and
    // folds `dest.sub` into `dest.cat`'s list (sorted, same convention) when
    // one was given.
    case 'REMOVE_SUBCAT': {
      const {cat, sub, dest} = action
      const memberCount = state.skus.filter((s) => s.cat === cat && s.sub === sub).length
      if (memberCount > 0 && !dest) {
        throw new Error('REMOVE_SUBCAT: a destination is required to move this sub-category\'s SKUs')
      }

      const existing = state.subcats[cat]
      const subcats = {...state.subcats}
      if (existing) {
        const next = existing.filter((s) => s !== sub)
        if (next.length) subcats[cat] = next
        else delete subcats[cat]
      }
      if (dest) {
        const list = subcats[dest.cat] || []
        subcats[dest.cat] = list.includes(dest.sub) ? list : [...list, dest.sub].sort()
      }

      const skus = dest
        ? state.skus.map((s) => (s.cat === cat && s.sub === sub ? {...s, cat: dest.cat, sub: dest.sub} : s))
        : state.skus

      return {...state, subcats, skus}
    }

    // Renames `from` to `to` within `cat` in place. Every SKU carrying it
    // follows, and so do the freeze requests that reference it — but ONLY
    // those at level 'Sub-category'; a SKU-level request whose subject
    // happens to read the same name is a different thing entirely and must
    // not be rewritten. Unlike REMOVE_SUBCAT this never orphans anything —
    // renaming always keeps the same members, just under a new name — so
    // there is no destination to require.
    case 'RENAME_SUBCAT': {
      const {cat, from, to} = action
      const existing = state.subcats[cat] || []
      const renamed = existing.map((s) => (s === from ? to : s))
      const subcats = {
        ...state.subcats,
        [cat]: (renamed.includes(to) ? renamed : [...renamed, to]).sort(),
      }
      const skus = state.skus.map((s) => (s.cat === cat && s.sub === from ? {...s, sub: to} : s))
      const requests = state.requests.map((r) => (
        r.level === 'Sub-category' && r.subject === from ? {...r, subject: to} : r
      ))
      return {...state, subcats, skus, requests}
    }

    // Appends a new DWS evidence record to the session-only `uploads` list
    // (see initialState() above — never into the seeded `dwsRecords`).
    // `record.sku` is the row's identifying key using the SAME overloaded
    // convention the frozen dataset itself uses: a real SKU's `sku` code, OR
    // a combo's `key` string (see gen-nobero-freeze.js's
    // `genComboUploadRecord`, which stores `sku: combo.key`).
    //
    // Deliberately does NOT touch `merchantCount` on any row. `merchantCount`
    // is the seeded baseline; `state/dwsEvidence.js`'s own `dwsEvidence`
    // computes the live count at read time as
    // `row.merchantCount + <session uploads for this row>` — it already adds
    // this session's uploads on top of the baseline. Bumping `merchantCount`
    // here as well as appending to `uploads` would double-count every
    // session upload the instant a caller fed both `state.skus`/`state.combos`
    // AND `state.uploads` to `dwsEvidence` — exactly the shape a real reader
    // (a page rendering rows from this store, or Initiate's own submit gate)
    // uses. `skus`/`combos` are returned unchanged so a caller can safely read
    // rows straight off the store instead of working around this action.
    case 'ADD_UPLOAD': {
      const {record} = action
      const full = {source: 'upload', ...record}
      const uploads = [...state.uploads, full]
      return {...state, uploads}
    }

    // Appends a new freeze request as-is — Initiate (or a future caller)
    // is responsible for assembling a well-formed request object (id,
    // level, subject, kind, history, ...); this store just makes sure it
    // lands in the one `requests` array every section reads.
    case 'ADD_REQUEST': {
      const requests = [...state.requests, action.request]
      return {...state, requests}
    }

    // Merchant's response to an "Additional data requested" (or "Awaiting
    // Prozo review") cycle. Delegates ENTIRELY to requests/logic.js's own
    // `applySubmission` — the pure, unit-tested implementation of the
    // 3-submission budget, the round advance, the state flip and the
    // NOTE/LIMIT_REACHED history stamping. This case used to carry a second,
    // simpler copy of that logic inline (bump `submissions`, flip `state`,
    // append one SUBMITTED entry) with no round advance, no cap and no
    // NOTE/LIMIT_REACHED events — so a merchant could submit past the
    // 3-submission budget and the round counter would never move, silently
    // disagreeing with the very rules `applySubmission` enforces elsewhere
    // in this module. That drift is exactly what Section 0 exists to stop:
    // Catalogue and Packing's own actions above already delegate to their
    // sections' pure functions (`applyMerge`, `UPSERT_PACK`'s cascade via
    // `chargeableKg`, etc.) rather than re-implementing them here, and this
    // case now follows the same precedent. `payload` is `{kindLabel, ships,
    // note}` — built by RqSubmitDrawer (requests/RqSubmitDrawer.js) — and is
    // passed straight through, never reshaped here. A request already at
    // the 3-submission cap is a no-op: `applySubmission` returns the SAME
    // object, so `.map` below leaves that row's reference (and every other
    // row) untouched.
    case 'SUBMIT_ADDITIONAL_DATA': {
      const {requestId, payload} = action
      const requests = state.requests.map((r) => (
        r.id === requestId
          ? applySubmission(r, payload || {}, {actor: catalogueActor(), today: todayISO()})
          : r
      ))
      return {...state, requests}
    }

    // Live mode: the backend's list (already mapped by data/mapRequest.js) replaces the whole
    // collection; a single server response replaces the one request it is about, matched on the
    // human reference or the backend key.
    // Live plumbing, exactly like SET_REQUESTS: the server is the source of truth for these three
    // slices, and the reducer only ever holds what came back from it.
    case 'SET_CATALOGUE':
      return {...state, skus: action.skus || []}
    case 'SET_PACKS':
      return {...state, packs: action.packs || []}
    case 'SET_SUBCATS':
      return {...state, subcats: action.subcats || {}}
    case 'SET_REQUESTS': {
      return {...state, requests: Array.isArray(action.requests) ? action.requests : []}
    }

    case 'REPLACE_REQUEST': {
      const {request} = action
      if (!request) return state
      const hit = state.requests.some((r) => r.id === request.id || (request.backendId && r.backendId === request.backendId))
      const requests = hit
        ? state.requests.map((r) => (r.id === request.id || (request.backendId && r.backendId === request.backendId) ? request : r))
        : [...state.requests, request]
      return {...state, requests}
    }

    // The merchant's answer to a Prozo auto-proposal (seeded mode; live mode replaces the row
    // with the server's own). Accepting makes it the approved freeze — declaration included, T23
    // notice attached, billing still inert; declining closes it as a rejected-tab item with a reason.
    case 'ACCEPT_PROPOSAL': {
      const today = todayISO()
      const actor = catalogueActor()
      const requests = state.requests.map((r) => {
        if (r.id !== action.requestId || r.kind !== 'review') return r
        const round = r.round || 1
        return {
          ...r,
          kind: 'approved',
          state: undefined,
          approvedBy: actor,
          effectiveFrom: today,
          cyclesElapsed: 0,
          approvalNotice: APPROVAL_NOTICE,
          history: (r.history || []).concat([
            {date: today, actor, round, kind: EVENT_KIND.PROPOSAL_ACCEPTED, text: 'Proposal accepted. ' + APPROVAL_NOTICE},
            {date: today, actor: 'System', round, kind: EVENT_KIND.APPROVED, text: 'Approved — proposed by Prozo, accepted by the merchant.'},
          ]),
        }
      })
      return {...state, requests}
    }

    case 'DECLINE_PROPOSAL': {
      const today = todayISO()
      const actor = catalogueActor()
      const note = String(action.remark || '').trim()
      const requests = state.requests.map((r) => {
        if (r.id !== action.requestId || r.kind !== 'review') return r
        return {
          ...r,
          kind: 'rejected',
          state: undefined,
          reason: "You declined Prozo's proposal",
          decidedOn: today,
          history: (r.history || []).concat([{
            date: today, actor, round: r.round || 1, kind: EVENT_KIND.PROPOSAL_DECLINED,
            text: 'Proposal declined' + (note ? ': ' + note : '.'),
          }]),
        }
      })
      return {...state, requests}
    }

    default:
      return state
  }
}

/**
 * `useFreezeStore()` → `{skus, combos, packs, subcats, dwsRecords, requests,
 * uploads, mergeCandidates, mapLog, catalogueSyncing, actor, actions}`.
 * Instantiate this ONCE, above the section switch (see index.js), and thread
 * the result down — never call it again per-section, or each section is
 * right back to its own island of state.
 */
export function useFreezeStore({live = false} = {}) {
  const [state, dispatch] = React.useReducer(reducer, live, initialState)

  // Owns the OMS/WMS pull's simulated latency. A ref on THIS hook, not on
  // whichever component called `pullFromOms` — `useFreezeStore()` lives
  // above the section switch and is never unmounted by a rail click, so the
  // scheduled `APPLY_OMS_PULL` still fires (and the store still updates)
  // even if the merchant has already navigated off Catalogue.
  const omsTimerRef = React.useRef(null)
  React.useEffect(() => () => {
    if (omsTimerRef.current) clearTimeout(omsTimerRef.current)
  }, [])

  const actions = React.useMemo(() => ({
    mergeSkus: (mother, codes) => dispatch({type: 'MERGE_SKUS', mother, codes}),
    undoMergeSku: (entryId) => dispatch({type: 'UNDO_MERGE_SKU', entryId}),
    importCatalogue: (rows) => dispatch({type: 'IMPORT_CATALOGUE', rows}),
    pullFromOms: () => {
      dispatch({type: 'START_OMS_PULL'})
      omsTimerRef.current = setTimeout(() => dispatch({type: 'APPLY_OMS_PULL'}), OMS_LATENCY_MS)
    },
    reviewSku: (code, patch) => dispatch({type: 'REVIEW_SKU', code, patch}),
    markSkusReviewed: (codes) => dispatch({type: 'MARK_SKUS_REVIEWED', codes}),
    fixSkuCode: (code, nextCode) => dispatch({type: 'FIX_SKU_CODE', code, nextCode}),
    updateSku: (sku, patch) => dispatch({type: 'UPDATE_SKU', sku, patch}),
    upsertPack: (patch) => dispatch({type: 'UPSERT_PACK', patch}),
    removePack: (id, destId) => dispatch({type: 'REMOVE_PACK', id, destId}),
    upsertSubcat: (cat, sub) => dispatch({type: 'UPSERT_SUBCAT', cat, sub}),
    removeSubcat: (cat, sub, dest) => dispatch({type: 'REMOVE_SUBCAT', cat, sub, dest}),
    renameSubcat: (cat, from, to) => dispatch({type: 'RENAME_SUBCAT', cat, from, to}),
    addUpload: (record) => dispatch({type: 'ADD_UPLOAD', record}),
    addRequest: (request) => dispatch({type: 'ADD_REQUEST', request}),
    // `payload` is `{kindLabel, ships, note}` — see applySubmission
    // (requests/logic.js) and the SUBMIT_ADDITIONAL_DATA case above.
    submitAdditionalData: (requestId, payload = {}) => dispatch({type: 'SUBMIT_ADDITIONAL_DATA', requestId, payload}),
    // Live-mode plumbing (index.js feeds these from data/api.js + data/mapRequest.js).
    setRequests: (requests) => dispatch({type: 'SET_REQUESTS', requests}),
    setCatalogue: (skus) => dispatch({type: 'SET_CATALOGUE', skus}),
    setPacks: (packs) => dispatch({type: 'SET_PACKS', packs}),
    setSubcats: (subcats) => dispatch({type: 'SET_SUBCATS', subcats}),
    replaceRequest: (request) => dispatch({type: 'REPLACE_REQUEST', request}),
    // The merchant's two answers to a Prozo auto-proposal (local transitions; index.js overrides
    // them with the API-backed versions on a live mount).
    acceptProposal: (requestId) => dispatch({type: 'ACCEPT_PROPOSAL', requestId}),
    declineProposal: (requestId, remark = '') => dispatch({type: 'DECLINE_PROPOSAL', requestId, remark}),
  }), [])

  return {...state, actions, actor: catalogueActor()}
}

export default useFreezeStore
